import React from 'react'
import PaymentMethodForm from '../../common/components/payment-method-new/PaymentMethodForm';
import SpinnerOverlay from '../../common/components/SpinnerOverlay';

import {axiosPost, axiosGet} from '../../common/js/axios-utility'

import useLoginCookie from '../../common/hooks/useLoginCookie';
import useCreatePaymentMethod from '../../common/hooks/useCreatePaymentMethod';

import { useQuery } from '@tanstack/react-query';
import { CrumplePopSignupContext } from '../contexts/CrumplePopSignupContext';
import { errorToast } from '../../common/js/toast-utility';
import MigratrionLoadingPage from './MigrationLoadingPage';

export default function CrumplePopSubscriptionMigration({customerInfo, onComplete}) {
    const [migrationState, setMigrationState] = React.useContext(CrumplePopSignupContext);
    const [processing, setProcessing] = React.useState(false);
    const [migrationPage, setMigrationPage] = React.useState('payment_method');

    const [pollData, setPollData] = React.useState('initializing');
    const login = useLoginCookie();
    const { saveCard } = useCreatePaymentMethod();

    React.useEffect(() => {
        if (migrationState.migrationInProgress){
            setMigrationPage('polling');
        }
    }, [migrationState.migrationInProgress])

    let content = <></>

    const hideSpinner = !processing && !customerInfo.isFetching;
    const customer = customerInfo.data;

    const handlePaymentMethodSubmission = async (payload) => {
        setMigrationState({...migrationState, processing: true});

        try {
            let pm;
            if (payload.isNewCard){
                pm = await saveCard(payload, customer);
            }
            else{
                pm = customer.paymentMethods.find(c => {
                    return c.id == payload.selectedId;
                })
            }
            await requestMigration(pm);
        }
        catch(err){
            console.error(err);
            errorToast('Failed to process payment method.');
            setMigrationState({...migrationState, processing: false});
        }
    }

    const requestMigration = async paymentMethod => {
        const payload = {
            contactId: customer.contactId,
            paymentMethodId: paymentMethod.id,
            email: customer.email,
            firstName: customer.firstName,
            lastName: customer.lastName,
            isTest: customer.blackthornGateway?.toLowerCase().includes('test'),
            subscriptions: migrationState.cpopUser.subscriptions
        };

        const {data} = await axiosPost('/cpop/migrate', payload)
        setMigrationState({...migrationState, processing: false});
        setMigrationPage('polling');
    }

    const handlePoll = res => {
        if (!res) return 2000;
        if (res.status != 'complete' && res.status != 'error'){
            return 2000
        }
        else{
            return 0;
        }
    }

    const result = useQuery({
        queryKey: ['migration', 'polling'],
        queryFn: pollOrderStatus,
        enabled: migrationPage == 'polling',
        refetchInterval : res => {
            return handlePoll(res)
        }
    });

    async function pollOrderStatus(){
        try{
            const data = await fetchMigrationStatus();
            if (!data) return;
            setPollData(data);
            if (data.status == 'complete'){
                onComplete();
            }
            else if (data.status == 'error' || data.status == 'processing required'){
               setMigrationPage('error');
            }
            return data;
        }
        catch(err){
            console.error(err);
            errorToast('Something went wrong. Please review your information and try again.');
            setMigrationState({...migrationState, processing: false});
            setMigrationPage('payment_method');
        }
    }

    const fetchMigrationStatus = async () => {
        const {data} = await axiosGet('/cpop/poll', {auth: {username: login.email, password: login.token}});
        return data;
    }

    return (
        <div>
            {migrationPage == 'payment_method' && (
            <SpinnerOverlay hidden={hideSpinner}>
                <PaymentMethodForm
                    customer={customer}
                    onSubmit={handlePaymentMethodSubmission}
                    submitButtonLabel="Migrate My Subscription" />
            </SpinnerOverlay>
            )}

            {migrationPage == 'polling' && (
                <MigratrionLoadingPage />
            )}
        </div>
    )
}
